import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import "@/App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import FileIntentBridge from "./components/FileIntentBridge";
import FeedbackWidget from "./components/FeedbackWidget";
import { isMobileApp } from "./lib/platform";

import Landing from "./pages/Landing";
import AuthCallback from "./pages/AuthCallback";

// Lazy component that renders synchronously once preloaded — required so that
// hydration of prerendered pages doesn't suspend (which would discard the
// static HTML and hurt LCP). index.js preloads the current route's chunk
// before calling hydrateRoot.
function lazyWithPreload(loader) {
    let Loaded = null;
    function Preloadable(props) {
        const [, force] = React.useReducer((x) => x + 1, 0);
        React.useEffect(() => {
            let live = true;
            if (!Loaded) loader().then((m) => { Loaded = m.default; if (live) force(); });
            return () => { live = false; };
        }, []);
        return Loaded ? React.createElement(Loaded, props) : null;
    }
    Preloadable.preload = () => loader().then((m) => { Loaded = m.default; return m; });
    return Preloadable;
}

const Toaster = React.lazy(() => import("sonner").then((m) => ({ default: m.Toaster })));

const Pricing = lazyWithPreload(() => import("./pages/Pricing"));
const About = lazyWithPreload(() => import("./pages/About"));
const Terms = lazyWithPreload(() => import("./pages/Terms"));
const Privacy = lazyWithPreload(() => import("./pages/Privacy"));
const Login = lazyWithPreload(() => import("./pages/Login"));
const Signup = lazyWithPreload(() => import("./pages/Signup"));
const ForgotPassword = lazyWithPreload(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyWithPreload(() => import("./pages/ResetPassword"));
const CsvEditor = lazyWithPreload(() => import("./pages/CsvEditor"));
const MyFiles = lazyWithPreload(() => import("./pages/MyFiles"));
const PaymentSuccess = lazyWithPreload(() => import("./pages/PaymentSuccess"));
const Account = lazyWithPreload(() => import("./pages/Account"));
const Admin = lazyWithPreload(() => import("./pages/Admin"));
const AdminInbox = lazyWithPreload(() => import("./pages/AdminInbox"));
const SharedView = lazyWithPreload(() => import("./pages/SharedView"));
const Faq = lazyWithPreload(() => import("./pages/Faq"));
const Blog = lazyWithPreload(() => import("./pages/Blog"));
const MobileUnlock = lazyWithPreload(() => import("./pages/MobileUnlock"));
const DeleteAccountPage = lazyWithPreload(() => import("./pages/DeleteAccount"));

const CsvViewer = lazyWithPreload(() => import("./pages/seo/CsvViewer"));
const CsvEditorPage = lazyWithPreload(() => import("./pages/seo/CsvEditor"));
const OpenCsvFiles = lazyWithPreload(() => import("./pages/seo/OpenCsvFiles"));
const WhatIsCsvFile = lazyWithPreload(() => import("./pages/seo/WhatIsCsvFile"));
const HowToEditCsvFiles = lazyWithPreload(() => import("./pages/seo/HowToEditCsvFiles"));
const CsvVsExcel = lazyWithPreload(() => import("./pages/seo/CsvVsExcel"));
const LightweightCsvEditor = lazyWithPreload(() => import("./pages/seo/LightweightCsvEditor"));
const PrivacyFocusedCsvViewer = lazyWithPreload(() => import("./pages/seo/PrivacyFocusedCsvViewer"));
const FastCsvViewer = lazyWithPreload(() => import("./pages/seo/FastCsvViewer"));
const CsvUseCases = lazyWithPreload(() => import("./pages/seo/CsvUseCases"));
const ImportExportCsv = lazyWithPreload(() => import("./pages/seo/ImportExportCsv"));
const Features = lazyWithPreload(() => import("./pages/seo/Features"));

const ToolsHub = lazyWithPreload(() => import("./pages/tools/ToolsHub"));
const ToolPage = lazyWithPreload(() => import("./pages/tools/ToolPage"));

const HowCsvFilesWork = lazyWithPreload(() => import("./pages/blog/HowCsvFilesWork"));
const BestCsvEditor = lazyWithPreload(() => import("./pages/blog/BestCsvEditor"));
const OpenLargeCsvFiles = lazyWithPreload(() => import("./pages/blog/OpenLargeCsvFiles"));
const WhyCsvStillMatters = lazyWithPreload(() => import("./pages/blog/WhyCsvStillMatters"));

// Map of prerendered public routes -> chunk preloader (used by index.js).
export const ROUTE_PRELOADERS = {
    "/pricing": Pricing.preload,
    "/about": About.preload,
    "/terms": Terms.preload,
    "/privacy": Privacy.preload,
    "/login": Login.preload,
    "/signup": Signup.preload,
    "/forgot-password": ForgotPassword.preload,
    "/faq": Faq.preload,
    "/blog": Blog.preload,
    "/csv-viewer": CsvViewer.preload,
    "/csv-editor": CsvEditorPage.preload,
    "/open-csv-files": OpenCsvFiles.preload,
    "/what-is-a-csv-file": WhatIsCsvFile.preload,
    "/how-to-edit-csv-files": HowToEditCsvFiles.preload,
    "/csv-vs-excel": CsvVsExcel.preload,
    "/lightweight-csv-editor": LightweightCsvEditor.preload,
    "/privacy-focused-csv-viewer": PrivacyFocusedCsvViewer.preload,
    "/fast-csv-viewer": FastCsvViewer.preload,
    "/csv-use-cases": CsvUseCases.preload,
    "/import-export-csv": ImportExportCsv.preload,
    "/features": Features.preload,
    "/blog/how-csv-files-work": HowCsvFilesWork.preload,
    "/blog/best-csv-editor": BestCsvEditor.preload,
    "/blog/open-large-csv-files": OpenLargeCsvFiles.preload,
    "/blog/why-csv-still-matters": WhyCsvStillMatters.preload,
    "/tools": ToolsHub.preload,
    "/delete-account": DeleteAccountPage.preload,
};
["csv-to-json", "json-to-csv", "csv-to-excel", "excel-to-csv", "csv-to-sql", "csv-to-markdown", "csv-to-html", "csv-delimiter-converter", "remove-duplicate-csv-rows", "csv-splitter", "csv-merger", "csv-validator"].forEach((s) => {
    ROUTE_PRELOADERS["/tools/" + s] = ToolPage.preload;
});

// Toaster mounts after idle so it never participates in hydration.
function DeferredToaster() {
    const [show, setShow] = React.useState(false);
    React.useEffect(() => {
        const cb = () => setShow(true);
        if ("requestIdleCallback" in window) window.requestIdleCallback(cb);
        else setTimeout(cb, 1200);
    }, []);
    if (!show) return null;
    return (
        <React.Suspense fallback={null}>
            <Toaster position="top-center" richColors />
        </React.Suspense>
    );
}

function Shell({ children }) {
    return (
        <div className="App flex flex-col min-h-screen">
            <Navbar />
            <main className="flex-1">{children}</main>
            <Footer />
        </div>
    );
}

// Minimal shell for the Android app — no marketing footer or full nav.
function MobileShell({ children }) {
    return (
        <div className="App flex flex-col min-h-screen">
            <Navbar />
            <main className="flex-1">{children}</main>
        </div>
    );
}

// Routes the home path inside the Android shell based on auth + paid state.
function MobileHomeRedirect() {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="auth-loading">
                <div className="ui-heading text-sm text-ink-soft">Loading…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    // has_access covers lifetime owners AND active trials (C-01: an active
    // 14-day trial must reach the editor, not the "trial ended" screen).
    if (user.has_access || user.role === "admin") return <Navigate to="/app" replace />;
    return <Navigate to="/unlock" replace />;
}

function OfflineBanner() {
    const { user } = useAuth();
    if (!user?.offline) return null;
    return (
        <div data-testid="offline-banner" style={{ background: "#B45309", color: "#fff", textAlign: "center", fontSize: 12, padding: "4px 10px" }}>
            Offline — your work stays on this device. Sign-in state restored from your last session.
        </div>
    );
}

function MobileRouter() {
    return (
        <>
            <OfflineBanner />
            <ScrollToTop />
            <FileIntentBridge />
            <React.Suspense fallback={null}>
                <Routes>
                    <Route path="/" element={<MobileHomeRedirect />} />
                    <Route path="/login" element={<MobileShell><Login /></MobileShell>} />
                    <Route path="/signup" element={<MobileShell><Signup /></MobileShell>} />
                    <Route path="/forgot-password" element={<MobileShell><ForgotPassword /></MobileShell>} />
                    <Route path="/reset-password" element={<MobileShell><ResetPassword /></MobileShell>} />
                    <Route path="/auth/google" element={<AuthCallback />} />
                    <Route path="/unlock" element={<MobileShell><ProtectedRoute><MobileUnlock /></ProtectedRoute></MobileShell>} />
                    <Route path="/account" element={<MobileShell><ProtectedRoute><Account /></ProtectedRoute></MobileShell>} />
                    <Route path="/app" element={<MobileShell><ProtectedRoute requirePaidMobile><CsvEditor /></ProtectedRoute></MobileShell>} />
                    <Route path="/files" element={<MobileShell><ProtectedRoute requirePaidMobile><MyFiles /></ProtectedRoute></MobileShell>} />
                    <Route path="/payment-success" element={<MobileShell><ProtectedRoute><PaymentSuccess /></ProtectedRoute></MobileShell>} />
                    <Route path="/admin" element={<MobileShell><ProtectedRoute adminOnly><Admin /></ProtectedRoute></MobileShell>} />
                    {/* Public pages linked from signup / nav — must exist in the app too (M-02, C-01) */}
                    <Route path="/terms" element={<MobileShell><Terms /></MobileShell>} />
                    <Route path="/privacy" element={<MobileShell><Privacy /></MobileShell>} />
                    <Route path="/tools" element={<MobileShell><ToolsHub /></MobileShell>} />
                    <Route path="/tools/:slug" element={<MobileShell><ToolPage /></MobileShell>} />
                    <Route path="/s/:token" element={<SharedView />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </React.Suspense>
        </>
    );
}

function AppRouter() {
    // Inside the Android shell, use the slimmed-down router (no marketing,
    // unpaid users get the IAP unlock screen instead of Stripe redirect).
    if (isMobileApp()) {
        return <MobileRouter />;
    }
    return (
        <>
            <ScrollToTop />
            <FileIntentBridge />
            <Routes>
                <Route path="/" element={<Shell><Landing /></Shell>} />
                <Route path="/pricing" element={<Shell><Pricing /></Shell>} />
                <Route path="/about" element={<Shell><About /></Shell>} />
                <Route path="/terms" element={<Shell><Terms /></Shell>} />
                <Route path="/privacy" element={<Shell><Privacy /></Shell>} />
                <Route path="/delete-account" element={<Shell><DeleteAccountPage /></Shell>} />
                <Route path="/login" element={<Shell><Login /></Shell>} />
                <Route path="/signup" element={<Shell><Signup /></Shell>} />
                <Route path="/forgot-password" element={<Shell><ForgotPassword /></Shell>} />
                <Route path="/reset-password" element={<Shell><ResetPassword /></Shell>} />
                <Route path="/auth/google" element={<AuthCallback />} />
                <Route path="/payment-success" element={<Shell><ProtectedRoute><PaymentSuccess /></ProtectedRoute></Shell>} />
                <Route path="/account" element={<Shell><ProtectedRoute><Account /></ProtectedRoute></Shell>} />
                <Route path="/files" element={<Shell><ProtectedRoute requirePaid><MyFiles /></ProtectedRoute></Shell>} />
                <Route path="/app" element={<Shell><ProtectedRoute requirePaid><CsvEditor /></ProtectedRoute></Shell>} />
                <Route path="/admin" element={<Shell><ProtectedRoute adminOnly><Admin /></ProtectedRoute></Shell>} />
                <Route path="/admin/inbox" element={<Shell><ProtectedRoute adminOnly><AdminInbox /></ProtectedRoute></Shell>} />

                {/* SEO landing pages */}
                <Route path="/csv-viewer" element={<Shell><CsvViewer /></Shell>} />
                <Route path="/csv-editor" element={<Shell><CsvEditorPage /></Shell>} />
                <Route path="/open-csv-files" element={<Shell><OpenCsvFiles /></Shell>} />
                <Route path="/what-is-a-csv-file" element={<Shell><WhatIsCsvFile /></Shell>} />
                <Route path="/how-to-edit-csv-files" element={<Shell><HowToEditCsvFiles /></Shell>} />
                <Route path="/csv-vs-excel" element={<Shell><CsvVsExcel /></Shell>} />
                <Route path="/lightweight-csv-editor" element={<Shell><LightweightCsvEditor /></Shell>} />
                <Route path="/privacy-focused-csv-viewer" element={<Shell><PrivacyFocusedCsvViewer /></Shell>} />
                <Route path="/fast-csv-viewer" element={<Shell><FastCsvViewer /></Shell>} />
                <Route path="/csv-use-cases" element={<Shell><CsvUseCases /></Shell>} />
                <Route path="/import-export-csv" element={<Shell><ImportExportCsv /></Shell>} />
                <Route path="/features" element={<Shell><Features /></Shell>} />

                {/* Free tools */}
                <Route path="/tools" element={<Shell><ToolsHub /></Shell>} />
                <Route path="/tools/:slug" element={<Shell><ToolPage /></Shell>} />

                {/* Resources */}
                <Route path="/faq" element={<Shell><Faq /></Shell>} />
                <Route path="/blog" element={<Shell><Blog /></Shell>} />
                <Route path="/blog/how-csv-files-work" element={<Shell><HowCsvFilesWork /></Shell>} />
                <Route path="/blog/best-csv-editor" element={<Shell><BestCsvEditor /></Shell>} />
                <Route path="/blog/open-large-csv-files" element={<Shell><OpenLargeCsvFiles /></Shell>} />
                <Route path="/blog/why-csv-still-matters" element={<Shell><WhyCsvStillMatters /></Shell>} />

                <Route path="/s/:token" element={<SharedView />} />
                <Route path="*" element={<Shell><Landing /></Shell>} />
            </Routes>
        </>
    );
}

export default function App() {
    return (
        <HelmetProvider>
            <BrowserRouter>
                <AuthProvider>
                    <AppRouter />
                    <FeedbackWidget />
                    <DeferredToaster />
                </AuthProvider>
            </BrowserRouter>
        </HelmetProvider>
    );
}
